import React, { useState, useEffect } from 'react'
import { Row, Col, message } from 'antd'
import QueryInterface from '../components/QueryInterface'
import QueryResults from '../components/QueryResults'
import { apiService } from '../services/api'
import { QueryRequest, QueryResult, SystemInfo } from '../types/api'

interface QueryPageProps {
  systemInfo: SystemInfo | null
}

// 本地存储键名
const QUERY_RESULT_STORAGE_KEY = 'ai_query_result'
const QUERY_TIMESTAMP_STORAGE_KEY = 'ai_query_timestamp'
const QUERY_RESULT_EXPIRY_TIME = 24 * 60 * 60 * 1000 // 24小时过期

const QueryPage: React.FC<QueryPageProps> = () => {
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)

  // 页面加载时恢复查询结果
  useEffect(() => {
    restoreQueryResult()
  }, [])

  // 保存查询结果到本地存储
  const saveQueryResult = (result: QueryResult) => {
    try {
      const timestamp = Date.now()
      localStorage.setItem(QUERY_RESULT_STORAGE_KEY, JSON.stringify(result))
      localStorage.setItem(QUERY_TIMESTAMP_STORAGE_KEY, timestamp.toString())
    } catch (error) {
      console.warn('保存查询结果失败:', error)
    }
  }

  // 从本地存储恢复查询结果
  const restoreQueryResult = () => {
    try {
      const savedResult = localStorage.getItem(QUERY_RESULT_STORAGE_KEY)
      const savedTimestamp = localStorage.getItem(QUERY_TIMESTAMP_STORAGE_KEY)

      if (savedResult && savedTimestamp) {
        const timestamp = parseInt(savedTimestamp)
        const now = Date.now()

        // 检查是否过期
        if (now - timestamp < QUERY_RESULT_EXPIRY_TIME) {
          const result: QueryResult = JSON.parse(savedResult)
          setQueryResult(result)
          console.log('已恢复上次查询结果')
        } else {
          // 清除过期数据
          clearSavedQueryResult()
        }
      }
    } catch (error) {
      console.warn('恢复查询结果失败:', error)
      clearSavedQueryResult()
    }
  }

  // 清除保存的查询结果
  const clearSavedQueryResult = () => {
    localStorage.removeItem(QUERY_RESULT_STORAGE_KEY)
    localStorage.removeItem(QUERY_TIMESTAMP_STORAGE_KEY)
  }

  const handleQuery = async (request: QueryRequest) => {
    setLoading(true)
    setQueryResult(null)
    setProgress(0)

    // Progress simulation for better UX
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 10, 90))
    }, 2000)

    try {
      const result = await apiService.naturalLanguageQuery(request)
      setProgress(100)
      setQueryResult(result)

      // 保存查询结果到本地存储
      saveQueryResult(result)

      if (result.success) {
        message.success(`查询成功，返回 ${result.total_rows || 0} 条记录`)
      } else {
        message.error(`查询失败: ${result.error}`)
      }
    } catch (error: any) {
      console.error('查询失败:', error)

      let errorMessage = '查询失败'

      if (error.code === 'ECONNABORTED') {
        errorMessage = '查询超时，请尝试简化查询或稍后重试'
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }

      message.error(errorMessage)

      // 创建错误结果对象
      const errorResult = {
        success: false,
        query: request.query,
        error: errorMessage
      }
      setQueryResult(errorResult)

      // 也保存错误结果，以便用户返回时能看到
      saveQueryResult(errorResult)
    } finally {
      clearInterval(progressInterval)
      setLoading(false)
      setProgress(0)
    }
  }

  // 清除查询结果
  const handleClearResults = () => {
    setQueryResult(null)
    clearSavedQueryResult()
    message.info('已清除查询结果')
  }

  return (
    <div className="fade-in">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <QueryInterface
            onQuery={handleQuery}
            loading={loading}
            progress={progress}
          />
        </Col>

        <Col span={24}>
          <QueryResults
            result={queryResult}
            loading={loading}
            onClearResults={handleClearResults}
          />
        </Col>
      </Row>
    </div>
  )
}

export default QueryPage
